import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FormBuilder } from '@angular/forms';
const endpoint = environment.api_endpoint;
@Injectable({
  providedIn: 'root',
})
export class FormsServiceService {
  private formSchemaCache = new Map<string, Observable<any>>();

  constructor(
    private http: HttpClient,
    private formBuilder: FormBuilder,
  ) {}

  /** `skipAuth` sends the request without the Authorization header (login pages). */
  loadFormData(
    formId: string,
    recordId?: string,
    skipAuth = false,
  ): Observable<any> {
    const recordID =
      recordId === 'add' || recordId === 'invite' || !recordId ? 0 : recordId;

    if (recordID === 0 && this.formSchemaCache.has(formId)) {
      return this.formSchemaCache.get(formId)!;
    }

    const accessToken = localStorage.getItem('accessToken');

    let headers = new HttpHeaders(); // ✅ define in outer scope
    // include product header for backend routing/identification
    // headers = headers.set('X-Product-frontend', 'cc_frontend');

    if (accessToken && !skipAuth) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const req$ = this.http
      .get<any>(`${endpoint}/api/v1/form/data/${formId}/${recordID}`, {
        headers,
      })
      .pipe(
        shareReplay(1),
        catchError((err) => {
          if (recordID === 0) {
            this.formSchemaCache.delete(formId);
          }
          return throwError(() => err);
        }),
      );

    if (recordID === 0) {
      this.formSchemaCache.set(formId, req$);
    }

    return req$;
  }

  replaceTemplateTokens(
    template: string,
    values: { [key: string]: string },
  ): string {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      const trimmedKey = key.trim();
      return values[trimmedKey] || '';
    });
  }

  loadData(
    method: string,
    url: string,
    headersObj: any = {},
    body: any = {},
    limit?: number,
  ): Observable<any> {
    const accessToken = localStorage.getItem('accessToken'); // or wherever it's stored
    let params = new HttpParams();

    // Add limit if present
    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }

    // ✅ If method is GET and body has keys, add them to params
    if (
      method.toUpperCase() === 'GET' &&
      body &&
      Object.keys(body).length > 0
    ) {
      Object.keys(body).forEach((key) => {
        const val = body[key];
        const isEmpty =
          val === undefined ||
          val === null ||
          val === '' ||
          (Array.isArray(val) && val.length === 0);
        if (!isEmpty) {
          if (Array.isArray(val)) {
            val.forEach((item: any) => {
              if (item !== undefined && item !== null && item !== '') {
                const subItems =
                  typeof item === 'string' ? item.split(',') : [item];
                subItems.forEach((sub: any) => {
                  const trimmed = typeof sub === 'string' ? sub.trim() : sub;
                  if (trimmed !== '') {
                    params = params.append(key, trimmed);
                  }
                });
              }
            });
          } else if (typeof val === 'string' && val.includes(',')) {
            val.split(',').forEach((sub: string) => {
              const trimmed = sub.trim();
              if (trimmed !== '') {
                params = params.append(key, trimmed);
              }
            });
          } else {
            params = params.append(key, val);
          }
        }
      });
    }

    const headers = new HttpHeaders({
      ...headersObj,
      Authorization: `Bearer ${accessToken}`,
      // 'X-Product-frontend': 'cc_frontend',
      'Content-Type': 'application/json',
    });

    const options = { headers, params };

    switch (method.toUpperCase()) {
      case 'GET':
        return this.http
          .get(url, options)
          .pipe(catchError((err) => throwError(() => err)));
      case 'POST':
        return this.http
          .post(url, body, options)
          .pipe(catchError((err) => throwError(() => err)));
      case 'PUT':
        return this.http
          .put(url, body, options)
          .pipe(catchError((err) => throwError(() => err)));
      case 'DELETE':
        return this.http
          .delete(url, options)
          .pipe(catchError((err) => throwError(() => err)));
      default:
        return throwError(
          () => new Error(`Unsupported HTTP method: ${method}`),
        );
    }
  }

  submitFormData(
    apiIntegration: { url: string; method: string },
    payload: any,
    skipAuth = false,
  ): Observable<any> {
    const accessToken = localStorage.getItem('accessToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(skipAuth ? {} : { Authorization: `Bearer ${accessToken}` }),
      // 'X-Product-frontend': 'cc_frontend',
    });
    if (apiIntegration.url != '' && apiIntegration.method != '') {
      switch (apiIntegration.method) {
        case 'POST':
          return this.http.post(apiIntegration.url, payload, { headers });
        case 'PUT':
          return this.http.put(apiIntegration.url, payload, { headers });
        case 'PATCH':
          return this.http.patch(apiIntegration.url, payload, { headers });
        case 'DELETE':
          return this.http.delete(apiIntegration.url, { headers });
        case 'GET':
          return this.http.get(apiIntegration.url, { headers });
        default:
          throw new Error(`Unsupported HTTP method: ${apiIntegration.method}`);
      }
    } else {
      return this.http.post(`${endpoint}/api/v1/form/data`, payload, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          ...(skipAuth ? {} : { Authorization: `Bearer ${accessToken}` }),
          // 'X-Product-frontend': 'cc_frontend',
        }),
      });
    }
  }

  fieldAvailabilityChecker(payload: any): Observable<any> {
    const accessToken = localStorage.getItem('accessToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      // 'X-Product-frontend': 'cc_frontend',
    });
    return this.http.post<any>(
      endpoint + '/api/v1/form/data/check-value',
      payload,
      { headers },
    );
  }

  loadAggregationData(payload: any): Observable<any> {
    const accessToken = localStorage.getItem('accessToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      // 'X-Product-frontend': 'cc_frontend',
    });
    return this.http.post<any>(
      endpoint + '/analytics-service/v1/cards',
      payload,
      {
        headers,
      },
    );
  }
}
